<script setup>

import { inject, ref, computed, provide, watch } from 'vue';
import { 
	estChampHerite, 
	estChampSurcharge,
	getTypeOccurrenceLien } from '@/scripts/global.js';
import { 
	apiModifierInfosChamp,
	apiSurchargerChamps,
	apiSupprimerChamps } from '@/services/ServiceModele.js';
import SelecteurTypeDonnee from '@/components/generiques/SelecteurTypeDonnee.vue';
import SelecteurTypeOccurrence from '@/components/generiques/SelecteurTypeOccurrence.vue';
import SelecteurModeEvaluation from '@/components/generiques/SelecteurModeEvaluation.vue';
import FormuleAffichee from '@/components/generiques/FormuleAffichee.vue';
import Tag from 'primevue/tag';

const emit = defineEmits(['editionChamp']);

const { typeObjet, champ } = defineProps({
	typeObjet: Object,
	champ: Object
});

const appelerApiModifModele = inject('appelerApiModifModele');
const getDimension = inject('getDimension');
const getTypeObjet = inject('getTypeObjet');
const getTypeDonnee = inject('getTypeDonnee');
const getChamp = inject('getChamp');
const typeObjetACleAuto = inject('typeObjetACleAuto');
const getChampsCles = inject('getChampsCles');
const selectionnerChamp = inject('selectionnerChamp');
const selectionSchema = inject('selectionSchema');
const afficherBarreFlottante = inject('afficherBarreFlottante');
const ouvrirFenetreFormule = inject('ouvrirFenetreFormule');

const infosChamp = ref(getInfosChamp(champ));

const boutonMenu = ref(null);

const estPopulation = computed(() => {
	return champ.type === 'ChampPopulation';
});

const estCle = computed(() => {
	return champ.modeEvaluation === 'CLE' || champ.modeEvaluation === 'CLE_AUTO';
});

const estHerite = computed(() => {
	return estChampHerite(typeObjet, champ);
});

const estSurcharge = computed(() => {
	return estChampSurcharge(champ);
});

const itemsMenu = computed(() => {
	const items = [];
	items.push({
		libelle: 'Editer',
		icones: ['fa fa-edit'],
		desactive: estHerite.value || estPopulation.value,
		groupe: 1, 
		commande: () => {
			editer();
		}
	});
	if (estHerite.value) {
		items.push({
			libelle: 'Surcharger',
			icones: ['fa fa-layer-group'],
			groupe: 2, 
			commande: () => {
				surcharger();
			}
		});
	}
	items.push({
		libelle: 'Supprimer',
		icones: ['fa fa-trash'],
		style: 'color: var(--couleur-supprimer); ',
		desactive: estHerite.value || estPopulation.value || estCle.value,
		groupe: 3, 
		commande: () => {
			supprimer();
		}
	});
	return items;
});

watch(() => getInfosChamp(champ), (infos) => {
	infosChamp.value = infos;
});

function getInfosChamp(champ) {
	if (!champ) {
		return null;
	}
    return {
        id: champ.id,
        idTypeObjet: champ.idTypeObjet,
        nom: champ.nom,
        typeDonnee: getTypeDonnee(champ.typeDonnee),
        typeLien: champ.typeLien,
        typeOccurrenceLien: getTypeOccurrenceLien(champ.typeLien),
        nomChampReciproque: getNomChampReciproque(champ),
        modeEvaluation: champ.modeEvaluation,
		dimensions: champ.dimensions.map(idDimension => getDimension(idDimension))
    };
}

function getNomChampReciproque(champ) {
    if (champ.typeDonnee?.classe === 'OBJET' && champ.idChampReciproque) {
        return getChamp(champ.typeDonnee.id, champ.idChampReciproque)?.nom;
    } else {
        return null;
    }
}

function getChampDTO(champ) {
	return {
        idTypeObjet: champ.idTypeObjet,
        id: champ.id ? champ.id : null,
        nom: champ.nom,
        typeDonnee: champ.typeDonnee,
        modeEvaluation: champ.modeEvaluation,
        dimensions: champ.dimensions.map(dim => dim.id),
        typeLien: champ.typeLien,
        nomChampReciproque: champ.nomChampReciproque
	};
}

function reinitialiserValeurs() {
	infosChamp.value = getInfosChamp(champ);
}

function basculerAffichageMenu() {
    afficherBarreFlottante({
        items: itemsMenu, 
        selectionItems: [],
        composantParent: boutonMenu.value
    });
}

function afficherModaleEditionChamp() {
	emit('editionChamp', champ);
}

function getChampReciproque(champ) {
	return getChamp(champ.typeDonnee.id, champ.idChampReciproque);
}

function modifierNom() {
	appelerApiModifModele(apiModifierInfosChamp, getChampDTO(infosChamp.value), (reponseOK) => {
		if (reponseOK) {
			reinitialiserValeurs();
		}
	});
}

function modifierTypeDonnee(typeDonnee) {
	if (typeDonnee.classe === 'OBJET' && typeDonnee.id !== champ.typeDonnee.id) {
		const nouvellesInfosChamp = {};
		Object.assign(nouvellesInfosChamp, infosChamp.value);
		emit('editionChamp', nouvellesInfosChamp);
	} else {
		appelerApiModifModele(apiModifierInfosChamp, getChampDTO(infosChamp.value), () => {
			reinitialiserValeurs();
		});
	}
}

function modifierModeEvaluation(modeEvaluation) {
	if (modeEvaluation === 'CLE' && typeObjetACleAuto(typeObjet)) {
		if (!confirm(`Le champ '${getChampsCles(typeObjet)[0].nom}', actuellement un champ de clé automatique, deviendra un champ clé simple. Voulez-vous continuer ?`)) {
			return;
		}
	} else if (modeEvaluation === 'CLE_AUTO') {
		const autresChampsCles = getChampsCles(typeObjet).filter(chp => chp != champ);
		if (autresChampsCles.length > 0) {
			if (!confirm(`Les champs clés ${autresChampsCles.map(chp => chp.nom).join(', ')} ne seront plus des champs clés. Voulez-vous continuer ?`)) {
				return;
			}
		}
	}
	appelerApiModifModele(apiModifierInfosChamp, getChampDTO(infosChamp.value), (reponseOK) => {
		reinitialiserValeurs();
		if (reponseOK && modeEvaluation === 'CALCUL') {
			ouvrirFenetreFormule(champ, true);
		}
	});
}

function editer() {
	basculerAffichageMenu();
	afficherModaleEditionChamp();
}

function surcharger() {
	basculerAffichageMenu();
	appelerApiModifModele(apiSurchargerChamps, typeObjet, selectionSchema.value.champs);
}

function supprimer() {
	basculerAffichageMenu();
	appelerApiModifModele(apiSupprimerChamps, selectionSchema.value.champs);
}

</script>

<template>
	<div class="detail-champ" v-if="champ != null">		
		<template v-if="estHerite">
			<label class="intitule">Hérité de :</label>
			<button
				class="lien-interne" 
				@click="selectionnerChamp(getTypeObjet(champ.idTypeObjet), champ)">
				{{ getTypeObjet(champ.idTypeObjet).nom }}
			</button>
		</template>
		<template v-else-if="estSurcharge">
			<label class="intitule">Surcharge de :</label>
			<button
				class="lien-interne" 
				@click="selectionnerChamp(getTypeObjet(champ.idTypeObjetChampSurcharge), getChamp(champ.idTypeObjetChampSurcharge, champ.idChampSurcharge))">
				{{ getTypeObjet(champ.idTypeObjetChampSurcharge).nom }}
			</button>
		</template>
		<label class="intitule">Nom :</label>
		<div class="zone-nom">
			<input type="text" class="saisie-nom" spellcheck="false" 
				v-model="infosChamp.nom" 
				:disabled="estPopulation || estHerite || estSurcharge"
				@keyup.enter="modifierNom()"
				@blur="reinitialiserValeurs" />
			<button class="bouton fa-solid fa-caret-down" 
				ref="boutonMenu"
				@click.stop="basculerAffichageMenu" />
		</div>
		<label class="intitule">Type :</label>
		<SelecteurTypeDonnee 
		  v-model:typeDonnee="infosChamp.typeDonnee" 
		  :proposer-types-objets="typeObjet.typeOccurrence === 'PLUSIEURS'" 
		  :desactive="estPopulation || estHerite || estSurcharge || estCle"
		  @modification="modifierTypeDonnee"
		  @blur="reinitialiserValeurs" />
		<template v-if="champ.typeLien != null">
			<label class="intitule">Nb. d'objets liés :</label>
			<SelecteurTypeOccurrence
				:typeOccurrence="infosChamp.typeOccurrenceLien" 
				:desactive="true" />
			<label class="intitule">Champ récip. :</label>
			<button
				class="lien-interne" 
				@click="selectionnerChamp(getTypeObjet(champ.typeDonnee.id), getChampReciproque(champ))">
				{{ getChampReciproque(champ).nom }}
			</button>
		</template>
		<label class="intitule">Mode :</label>
		<SelecteurModeEvaluation 
		  v-model:modeEvaluation="infosChamp.modeEvaluation" 
		  :desactive="estHerite || estCle"
		  :proposer-mode-cle="estCle"
		  @modification="modifierModeEvaluation"
		  @blur="reinitialiserValeurs" />
		<label class="intitule">Dimensions :</label>
		<div class="liste-badges"
			:class="{desactive: estPopulation || estHerite || estSurcharge || estCle}" 
			tabindex="0"
			@click="afficherModaleEditionChamp">
			<label class="aucun"
			  v-if="champ.dimensions == null || champ.dimensions.length == 0">
			  	<i>Aucune</i>
			</label>
			<Tag v-for="idDimension in champ.dimensions" class="badge dimension">
				{{ getDimension(idDimension).nom }}
			</Tag>
		</div>
		<template v-if="champ.formule">
			<label class="intitule">Formule :</label>
			<div
				class="zone-apercu-formule"
				:class="{'formule-en-erreur': champ.formuleEnErreur}"
				@click="ouvrirFenetreFormule(champ)">
				<FormuleAffichee :formule="champ?.formule" class="apercu-formule" />
			</div>
		</template>
	</div>
</template>

<style scoped>

.detail-champ {
	display: grid;
	grid-template-columns: auto minmax(0, 1fr);
	gap: 2px;
	margin-top: 10px;
	margin-bottom: 20px;
	padding-left: 5px;
	align-items: start;
}

.zone-nom {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
}

.saisie-nom {
	margin-right: 2px;
	flex-grow: 1;
	min-width: 20px;
}

.liste-badges {
	border: solid 1px var(--couleur-bordure);
	border-radius: 2px;
	padding: 2px 5px;
	user-select: none;
	background-color: white;
	cursor: pointer;
}

.liste-badges:hover {
	border-color: var(--couleur-survol);
}

.badge {
	border: solid 1px var(--couleur-bordure);
	border-radius: 5px;
	user-select: none;
	padding: 5px 7px;
	font-weight:700 !important;
	font-size: 8px !important;
}

.dimension {
	background-color: var(--couleur-dimension) !important;
	margin: 0px 1px;
}

.aucun {
	cursor: inherit;
}

.desactive {
	background-color: rgb(250, 250, 250);
	cursor: initial;
	border-color: var(--couleur-bordure) !important;
}

</style>